import React, { useEffect, useState, useRef, useMemo } from "react";
import { 
    Modal, ModalBody, Table, 
    Row, Col, FormGroup, Label, Input, Button
} from "reactstrap";
import { ContentLoader } from "../../../../components/Common";
import moment from "moment";
import { useReactToPrint } from "react-to-print";
import { useTenant } from "../../../../hooks/useTenant";
import TestResultService from "../../../../services/test_result_service";
import MemberService from "../../../../services/member_service";
import PrintableTestReport from "./PrintableTestReport";
import { GENDER_OPTIONS, GENDER_LABELS } from "../../../../constants/gender";

const TestReportModal = ({ isOpen, toggle, event }) => {
    const { idTenant, tenantName, tenantLogo } = useTenant();
    const printRef = useRef();
    
    const [loading, setLoading] = useState(true);
    const [allMembers, setAllMembers] = useState([]);
    const [rawResults, setRawResults] = useState([]);
    
    // Filters
    const [genderFilter, setGenderFilter] = useState('');
    const [ageFilter, setAgeFilter] = useState('');
    
    // Filtros aplicados via botao
    const [appliedGenderFilter, setAppliedGenderFilter] = useState('');
    const [appliedAgeFilter, setAppliedAgeFilter] = useState('');

    const handlePrint = useReactToPrint({
        contentRef: printRef,
        content: () => printRef.current,
        documentTitle: `Ranking_${event?.name || "Teste"}`
    });

    useEffect(() => {
        if (isOpen && event?.id && idTenant) {
            loadData();
        } else {
            setRawResults([]);
            setGenderFilter('');
            setAgeFilter('');
            setAppliedGenderFilter('');
            setAppliedAgeFilter('');
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isOpen, event?.id, idTenant]);

    const loadData = async () => {
        try {
            setLoading(true);
            const [membersData, testsData] = await Promise.all([
                MemberService.getMembers(idTenant),
                TestResultService.getResultsByEvent(idTenant, event.id)
            ]);

            setAllMembers(membersData || []);
            setRawResults(testsData || []);
        } catch (error) {
            console.error("Erro ao carregar dados do teste:", error);
        } finally {
            setLoading(false);
        }
    };

    const getAgeRange = (years) => {
        if (!years && years !== 0) return null;
        if (years <= 25) return "18-25";
        if (years <= 35) return "26-35";
        if (years <= 45) return "36-45";
        return "46+";
    };

    const testTypeVal = event?.testType?.id || event?.testType;
    const isTimeBased = testTypeVal === "fixed_distance" || testTypeVal === "time";

    const filteredAndRankedResults = useMemo(() => {
        let results = rawResults.map(res => {
            const member = allMembers.find(m => String(m.id) === String(res.idMember)) || {};
            
            // Usar dados desnormalizados caso existam, senão fallback para o member
            const birthDateStr = res.memberBirthDate || member.dateOfBirth || member.birthDate;
            const age = birthDateStr ? moment().diff(moment(birthDateStr), 'years') : null;
            
            const genderVal = res.memberGender?.id || res.memberGender?.value || res.memberGender || member?.gender?.id || member?.gender?.value || member?.gender;
            
            const fullName = res.memberName || member?.fullName || member?.name || "Desconhecido";

            return {
                ...res,
                fullName,
                age,
                ageGroup: getAgeRange(age),
                genderGroup: genderVal === 'M' || genderVal === 'Male' || genderVal === 'Masculino' ? 'M' : 
                             genderVal === 'F' || genderVal === 'Female' || genderVal === 'Feminino' ? 'F' : 'Outro'
            };
        });

        if (appliedGenderFilter) {
            results = results.filter(r => r.genderGroup === appliedGenderFilter);
        }
        
        if (appliedAgeFilter) {
            results = results.filter(r => r.ageGroup === appliedAgeFilter);
        }

        return results.sort((a, b) => {
            if (isTimeBased) {
                const tA = parseFloat(a.resultTime || 0);
                const tB = parseFloat(b.resultTime || 0);
                if (tA === 0) return 1;
                if (tB === 0) return -1;
                return tA - tB;
            } else {
                const dA = parseFloat(a.resultDistance || a.resultTime || 0);
                const dB = parseFloat(b.resultDistance || b.resultTime || 0);
                return dB - dA;
            }
        });
    }, [rawResults, allMembers, appliedGenderFilter, appliedAgeFilter, isTimeBased]);

    const handleApplyFilters = () => {
        setAppliedGenderFilter(genderFilter);
        setAppliedAgeFilter(ageFilter);
    };

    const handleClearFilters = () => {
        setGenderFilter('');
        setAgeFilter('');
        setAppliedGenderFilter('');
        setAppliedAgeFilter('');
    };

    return (
        <React.Fragment>
            <Modal isOpen={isOpen} toggle={toggle} size="xl" scrollable>
                <div className="modal-header border-bottom d-flex align-items-center justify-content-between w-100">
                    <div className="d-flex align-items-center gap-2">
                        <i className="mdi mdi-format-list-numbered fs-4 text-primary"></i>
                        <h5 className="modal-title mb-0">Lista de Resultados - {event?.name}</h5>
                    </div>
                    <div className="d-flex align-items-center gap-3">
                        <button
                            className="btn btn-primary d-flex align-items-center justify-content-center rounded-2 shadow-sm p-0 border-0"
                            style={{ width: "38px", height: "38px" }}
                            onClick={handlePrint}
                            disabled={loading || rawResults?.length === 0}
                            title="Imprimir Ranking"
                        >
                            <i className="mdi mdi-printer font-size-18"></i>
                        </button>
                        <button type="button" className="btn-close m-0" onClick={toggle}></button>
                    </div>
                </div>
                
                <ModalBody className="p-4">
                    <div className="border-bottom pb-3 mb-4">
                        <div className="text-muted small">
                            <strong>Período:</strong> {event?.startDate ? moment(event.startDate).format("DD/MM/YYYY") : '--'} a {event?.endDate ? moment(event.endDate).format("DD/MM/YYYY") : '--'}
                            <span className="mx-2">|</span>
                            <strong>Categoria:</strong> {testTypeVal === 'fixed_time' ? `Tempo Fixo (${event?.fixedTime} min)` : testTypeVal === 'fixed_distance' ? `Distância Fixa (${event?.fixedDistance} m)` : "Performance Física"}
                        </div>
                    </div>

                    {loading ? (
                        <div className="text-center py-5">
                            <ContentLoader message="Carregando relatório..." />
                        </div>
                    ) : (
                        <>
                            <Row className="mb-4">
                                <Col md="4">
                                    <FormGroup>
                                        <Label className="fw-medium text-uppercase small text-muted">Filtrar por Sexo</Label>
                                        <Input type="select" value={genderFilter} onChange={e => setGenderFilter(e.target.value)} className="form-select border">
                                            <option value="">Todos</option>
                                            {GENDER_OPTIONS.map(opt => (
                                                <option key={opt.value} value={opt.value}>{opt.label}</option>
                                            ))}
                                        </Input>
                                    </FormGroup>
                                </Col>
                                <Col md="4">
                                    <FormGroup>
                                        <Label className="fw-medium text-uppercase small text-muted">Faixa de Idade</Label>
                                        <Input type="select" value={ageFilter} onChange={e => setAgeFilter(e.target.value)} className="form-select border">
                                            <option value="">Todas</option>
                                            <option value="18-25">Até 25 anos</option>
                                            <option value="26-35">26 a 35 anos</option>
                                            <option value="36-45">36 a 45 anos</option>
                                            <option value="46+">Acima de 45 anos</option>
                                        </Input>
                                    </FormGroup>
                                </Col>
                                <Col md="4" className="d-flex align-items-end mb-3 gap-2">
                                    <Button color="primary" onClick={handleApplyFilters} className="w-50">
                                        Aplicar Filtros
                                    </Button>
                                    <Button color="secondary" outline onClick={handleClearFilters} className="w-50">
                                        Limpar
                                    </Button>
                                </Col>
                            </Row>

                            {filteredAndRankedResults.length === 0 ? (
                                <div className="alert alert-warning text-center py-4">
                                    <i className="mdi mdi-alert-circle-outline fs-3 mb-2 d-block"></i>
                                    Não há resultados de teste registrados que correspondam a este evento/filtros.
                                </div>
                            ) : (
                                <>
                                    <div className="table-responsive">
                                        <Table striped bordered hover size="sm" className="align-middle mb-0" style={{ backgroundColor: '#fff' }}>
                                            <thead className="table-dark">
                                                <tr>
                                                    <th style={{ width: "6%" }} className="text-center">#</th>
                                                    <th style={{ width: "34%" }}>Atleta / Aluno</th>
                                                    <th style={{ width: "15%" }} className="text-center">Idade</th>
                                                    <th style={{ width: "15%" }} className="text-center">Sexo</th>
                                                    <th style={{ width: "15%" }} className="text-center">Resultado</th>
                                                    <th style={{ width: "15%" }} className="text-center">Avaliador</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {filteredAndRankedResults.map((res, index) => {
                                                    const pos = index + 1;
                                                    
                                                    return (
                                                        <tr key={res.id || index}>
                                                            <td className="text-center fw-bold text-muted">{pos}º</td>
                                                            <td className="fw-bold text-dark text-uppercase">{res.fullName}</td>
                                                            <td className="text-center text-muted">{res.age ? `${res.age} anos` : '-'}</td>
                                                            <td className="text-center">{GENDER_LABELS[res.genderGroup] || res.genderGroup || '-'}</td>
                                                            <td className="text-center fw-bold text-primary">
                                                                {isTimeBased ? `${res.resultTime}s` : `${res.resultDistance}`}
                                                            </td>
                                                            <td className="text-center text-muted small text-uppercase">
                                                                {res.employeeName || "Sistema"}
                                                            </td>
                                                        </tr>
                                                    );
                                                })}
                                            </tbody>
                                        </Table>
                                    </div>
                                    <div className="d-flex justify-content-between align-items-center mt-3 px-2 text-muted small">
                                        <span><strong>Total:</strong> {filteredAndRankedResults.length} resultados</span>
                                    </div>
                                </>
                            )}
                        </>
                    )}
                </ModalBody>
            </Modal>

            {/* Componente oculto para impressão */}
            <div style={{ display: 'none' }}>
                <PrintableTestReport 
                    ref={printRef}
                    event={event}
                    results={filteredAndRankedResults}
                    tenantName={tenantName}
                    tenantLogo={tenantLogo}
                />
            </div>
        </React.Fragment>
    );
};

export default TestReportModal;
